import React, { useEffect } from "react";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { cn } from "../../lib/utils";
import { useWorkflowStore } from "../../store/useWorkflowStore";
import InstanceSelector from "./selectors/InstanceSelector";
import ClientSelector from "./selectors/ClientSelector";
import DatabaseSelector from "./selectors/DatabaseSelector";
import FacilitySelector from "./selectors/FacilitySelector";
import { UseCaseSelector } from "./selectors/UseCaseSelector";
import ChecklistSelector from "../ChecklistSelector";

/**
 * WorkflowSourceForm component
 * 
 * Form for selecting source data for the workflow
 */
interface WorkflowSourceFormProps {
  onNext: () => void;
}

const WorkflowSourceForm: React.FC<WorkflowSourceFormProps> = ({ onNext }) => {
  const { 
    isFormValid, 
    setIsFormValid, 
    selectedServiceId, 
    selectedClient, 
    formData, 
    selectedFacilityId, 
    selectedUseCaseId, 
    selectedChecklists 
  } = useWorkflowStore();
  
  // Update form validity based on all required selections
  useEffect(() => {
    const isValid = 
      !!selectedServiceId && 
      !!selectedClient && 
      !!formData["databaseName"] && 
      !!selectedFacilityId && 
      !!selectedUseCaseId && 
      selectedChecklists.length > 0;
    
    setIsFormValid(isValid);
  }, [
    selectedServiceId, 
    selectedClient, 
    formData, 
    selectedFacilityId, 
    selectedUseCaseId, 
    selectedChecklists, 
    setIsFormValid
  ]);

  return (
    <div>
      <h2 className="text-xl font-semibold text-foreground mb-4">Source</h2>
      <p className="text-muted-foreground mb-6">
        Select the source instances for the migration.
      </p>

      <div className="flex flex-col space-y-4">
        <InstanceSelector />
        <ClientSelector />
        <DatabaseSelector />
        
        {/* Facility and Use Case selectors in 50/50 layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FacilitySelector className="w-full" />
          <UseCaseSelector className="w-full" />
        </div>
        <div className="mt-2 grid w-full items-center gap-1.5">
          <Label htmlFor="source-checklist">Checklists</Label>
          <ChecklistSelector />
        </div>
      </div>

      <div className="mt-8 flex justify-end">
        <Button
          onClick={onNext}
          disabled={!isFormValid}
          variant={isFormValid ? "default" : "outline"}
          className="px-6"
        >
          Next
        </Button>
      </div>
    </div>
  );
};

export default WorkflowSourceForm;
